import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import SFTypography from "../typography";
import { SFStack } from "../grid";
import useStyled from "../../hooks/useStyled";
import {
  SFCloseIcon,
  SFInfoRoundedIcon,
  SFNotificationIcon,
  SFWarningIcon,
  SFErrorIcon,
  SFCompletedIcon,
} from "../icons";
import SFHaloIconWrapper from "../icons/halo";
import { SFIconButton, SFLinkButton } from "../buttons";
import {
  Notification_Types,
  Notification_Actions,
} from "./../../constants/notificationTypes";
import { notificationActions } from "../../redux/reducers/notificationReducer";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { dismissNotification } from "./../../services/notificationService";

const { styled } = useStyled();

const StyledNotification = styled("div")(({ theme }) => ({
  padding: theme.spacing(1, 0.5, 1.5, 0.5),
  borderBottom: `0.5px solid ${theme.palette.grey450.main}`,
  height: "auto",
  ".icon-container": {
    display: "none",
  },
  ".date-container": {
    display: "block",
  },
  "&:hover": {
    background: theme.palette.grey450.main,
    ".icon-container": {
      display: "flex",
      width: 55,
      transform: "translateY(-10px)",
    },
    ".date-container": {
      display: "none",
    },
  },
  "& .button": {
    flexDirection: "column",
    alignItems: "initial",
    padding: theme.spacing(3),
    width: "100%",
    border: "none",
    background: "inherit",
    textAlign: "left",
    cursor: "pointer",
  },
}));

const StyledDetails = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: 380,
  "& a": {
    color: "#4EBEEB !important",
    textDecoration: "none",
  },
}));

const StyledStack = styled(SFStack)(({ theme }) => ({
  width: "100%",
  marginTop: theme.spacing(2),
}));

const NotificationIconStack = styled("div")(({ theme }) => ({
  margin: theme.spacing(0, 1.5, 0, 1),
}));

const NotificationStack = styled(SFStack)(({ theme }) => ({
  width: "100%",
}));

const SFNotifications = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleActionClick = (notif) => {
    switch (notif.customAction) {
    case Notification_Actions.NAVIGATE:
      navigate(notif.actionUrl);
      props.onClose();
      break;
    default:
      break;
    }
  };

  const handleDismiss = (notif) => {
    dismissNotification(notif.id).then(() => {
      dispatch(notificationActions.dismissNotification(notif.id));
    });
  };

  // Function to return the date label for the notification
  const getNotificationDateLabel = (createdAt, translate) => {
    // If the notification was created today, return "Today"
    if (moment().isSame(createdAt, "day")) {
      return translate("components.header.notificationPanel.today");
      // If the notification was created yesterday, return "Yesterday"
    } else if (moment().subtract(1, "days").isSame(createdAt, "day")) {
      return translate("components.header.notificationPanel.yesterday");
    } else {
      // Otherwise, return the date
      return moment(createdAt).format("D-MMM-YYYY");
    }
  };

  return (
    <StyledStack>
      {props.notifications.map((notif) => (
        <StyledNotification key={notif.id}>
          <SFStack direction="row">
            <NotificationIconStack alignItems="center">
              <NotificationICon type={notif.type} />
            </NotificationIconStack>
            <NotificationStack direction="column" spacing={1}>
              <SFStack
                justifyContent="space-between"
                direction="row"
                alignItems="center"
              >
                <SFTypography variant="courseCardDuration">
                  {notif.title}
                </SFTypography>
                <SFTypography className="date-container" variant="textLabel">
                  {getNotificationDateLabel(notif.createdAt, t)}
                </SFTypography>
              </SFStack>
              <SFStack direction="row">
                <SFStack direction="column">
                  <StyledDetails
                    onClick={(e) => {
                      // Check if the clicked element has an href attribute
                      if (e.target.href) props.onClose();
                    }}
                  >
                    <SFTypography variant="body1" hasHtml>
                      {notif.detail}
                    </SFTypography>
                  </StyledDetails>
                  {notif.actionUrl && (
                    <SFLinkButton
                      variant="cardSectionHeading"
                      text={notif.actionLabel}
                      color="blue200"
                      onClick={() => handleActionClick(notif)}
                    />
                  )}
                </SFStack>
                <SFStack
                  justifyContent="center"
                  alignItems="center"
                  className="icon-container"
                >
                  <SFIconButton
                    variant="transparent"
                    onClick={() => handleDismiss(notif)}
                  >
                    <SFCloseIcon color="white" />
                  </SFIconButton>
                </SFStack>
              </SFStack>
            </NotificationStack>
          </SFStack>
        </StyledNotification>
      ))}
    </StyledStack>
  );
};

const NotificationICon = (props) => {
  switch (props.type) {
  case Notification_Types.INFO:
    return (
      <SFHaloIconWrapper size="small" variant="grey">
        <SFInfoRoundedIcon size="small" />
      </SFHaloIconWrapper>
    );
  case Notification_Types.SUCCESS:
    return (
      <SFHaloIconWrapper size="small" variant="green">
        <SFCompletedIcon size="small" />
      </SFHaloIconWrapper>
    );
  case Notification_Types.WARNING:
    return (
      <SFHaloIconWrapper size="small" variant="orange">
        <SFWarningIcon size="small" />
      </SFHaloIconWrapper>
    );
  case Notification_Types.ERROR:
    return (
      <SFHaloIconWrapper size="small" variant="red">
        <SFErrorIcon size="small" />
      </SFHaloIconWrapper>
    );
  case Notification_Types.REMINDER:
    return (
      <SFHaloIconWrapper size="small" variant="blue">
        <SFNotificationIcon size="small" />
      </SFHaloIconWrapper>
    );
  default:
    return null;
  }
};

export default SFNotifications;
